import { Icon } from '@platform/components';
import { isTypeAccordionSection, TypeAccordionItemSkeleton } from '@platform/contentful';
import { Entry } from 'contentful';
import { useEffect, useRef, useState } from 'react';
import MarkdownText from './util/MarkdownText';

const AccordionItem = ({
  item,
}: {
  item: Entry<TypeAccordionItemSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'> | undefined;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [height, setHeight] = useState(0);
  const descriptionRef = useRef<HTMLDivElement>(null);

  // Measure content height for the expand/collapse animation
  useEffect(() => {
    if (descriptionRef.current) {
      setHeight(descriptionRef.current.getBoundingClientRect().height);
    }
  }, []);

  return (
    <div className='py-2.5 w-full sm:w-[600px] border-t-2 border-gray-100  text-left'>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className='inline-flex text-xl leading-10 cursor-pointer select-none hover:underline hover:text-brand-500'
      >
        <div className='mt-2 mr-2'>
          <Icon type={isOpen ? 'minus' : 'plus'} />
        </div>{' '}
        {item?.fields.header}
      </div>
      {/* This outer div masks the inner div and animates on max height to smoothly expand/collapse the content */}
      <div
        className='ml-2 overflow-hidden '
        style={isOpen ? { maxHeight: `${height}px` } : { maxHeight: '0px' }}
      >
        {/* This is always rendered & has a height so that the height can be measured on load for use in the the expand/collapse */}
        <div ref={descriptionRef} className='ml-6'>
          <MarkdownText text={item?.fields.description} />
        </div>
      </div>
    </div>
  );
};

const ContentfulAccordionSection = ({
  entry,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  entry: Entry<any, 'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'> | undefined;
}) => {
  if (!entry || !isTypeAccordionSection(entry)) {
    return null;
  }

  return (
    <div className='flex flex-col items-center text-center sm:mt-16 sm:mb-24'>
      <h1 className='my-3 font-header text-4xl sm:text-5xl leading-11 sm:leading-14 font-bold text-gray-500 text-center'>
        {entry.fields.header}
      </h1>
      <h2 className='font-header text-2xl leading-7 font-semibold text-gray-500 text-center'>
        {entry.fields.subHeader}
      </h2>
      <div className='mt-3'>
        {entry.fields.items?.map((item, index) => <AccordionItem key={index} item={item} />)}
      </div>
    </div>
  );
};

export default ContentfulAccordionSection;
